/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { IconCopy, Label, Text, TextInput } from '@sonarsource/echoes-react';
import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ClipboardIconButton } from '~shared/components/clipboard';
import { getCallbackUrl } from '~shared/helpers/urls';

interface OrganizationJiraFormProps {
  initialClientId?: string;
  onUpdateCredentials: (jiraAppCredentials: {
    jiraAppClientId: string;
    jiraAppSecret: string;
  }) => void;
}

export function OrganizationJiraForm({
  initialClientId = '',
  onUpdateCredentials,
}: Readonly<OrganizationJiraFormProps>) {
  const { formatMessage } = useIntl();

  const [jiraAppClientId, setJiraAppClientId] = useState(initialClientId);
  const [jiraAppSecret, setJiraAppSecret] = useState('');

  const jiraAppCredentials = useMemo(
    () => ({
      jiraAppClientId,
      jiraAppSecret,
    }),
    [jiraAppClientId, jiraAppSecret],
  );

  const deferredJiraAppCredentials = useDeferredValue(jiraAppCredentials);

  useEffect(() => {
    onUpdateCredentials({
      jiraAppClientId: deferredJiraAppCredentials.jiraAppClientId,
      jiraAppSecret: deferredJiraAppCredentials.jiraAppSecret,
    });
  }, [deferredJiraAppCredentials, onUpdateCredentials]);

  return (
    <div className="sw-flex sw-flex-col sw-gap-5">
      <div className="sw-flex sw-flex-col sw-gap-1/2">
        <Label>
          <FormattedMessage id="organization.jira.binding.app_configuration.callback_url.label" />
        </Label>
        <div className="sw-flex sw-items-center">
          <Text isHighlighted isSubtle>
            {getCallbackUrl()}
          </Text>
          <ClipboardIconButton
            Icon={IconCopy}
            aria-label={formatMessage({
              id: 'organization.jira.binding.app_configuration.callback_url.copy',
            })}
            className="sw-ml-2"
            copyValue={getCallbackUrl()}
            discreet
          />
        </div>
      </div>
      <TextInput
        id="jira-app-client-id"
        isRequired
        label={
          <FormattedMessage id="organization.jira.binding.app_configuration.client_id.label" />
        }
        onChange={(e) => {
          setJiraAppClientId(e.target.value);
        }}
        value={jiraAppClientId}
      />
      <TextInput
        id="jira-app-secret"
        isRequired
        label={
          <FormattedMessage id="organization.jira.binding.app_configuration.client_secret.label" />
        }
        onChange={(e) => {
          setJiraAppSecret(e.target.value);
        }}
        type="password"
        value={jiraAppSecret}
      />
    </div>
  );
}
